import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { propertyService } from '../../api/services/propertyService';
import { tokenStore } from '../../auth/tokenStore';
import type { AccessibleProperty } from '../../api/types/property.types';

interface PropertyContext {
  properties: AccessibleProperty[];
  selectedProperty: AccessibleProperty | null;
  selectedPropertyId: string | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
  /**
   * Switch to a different property. Persists to localStorage and invalidates
   * ALL queries so every screen re-fetches under the new property scope.
   */
  switchTo: (entityId: string) => void;
}

/**
 * Loads the list of properties accessible to the current session, keeps the
 * selected-property id in sync with localStorage, and exposes a `switchTo`
 * that safely swaps property scope for the whole app.
 *
 * Auto-selects the first property on first successful load if nothing is
 * persisted yet.
 */
export function usePropertyContext(): PropertyContext {
  const queryClient = useQueryClient();

  const query = useQuery<AccessibleProperty[]>({
    queryKey: ['properties', 'accessible'],
    queryFn: () => propertyService.getAccessible(),
    staleTime: 5 * 60_000, // properties rarely change during a session
    refetchOnWindowFocus: false,
  });

  const properties = useMemo(() => query.data ?? [], [query.data]);
  const stored = tokenStore.getSelectedPropertyId();

  // Effective selection: stored if it's still in the list, else first property.
  const selectedPropertyId = useMemo(() => {
    if (properties.length === 0) return stored;
    if (stored && properties.some((p) => p.entityId === stored)) return stored;
    return properties[0]?.entityId ?? null;
  }, [properties, stored]);

  // Persist the effective selection so the axios interceptor sees it on the
  // very next request (interceptor reads from tokenStore).
  if (selectedPropertyId && selectedPropertyId !== stored) {
    tokenStore.setSelectedPropertyId(selectedPropertyId);
  }

  const selectedProperty = useMemo(
    () => properties.find((p) => p.entityId === selectedPropertyId) ?? null,
    [properties, selectedPropertyId],
  );

  const switchTo = useCallback(
    (entityId: string) => {
      if (!entityId || entityId === tokenStore.getSelectedPropertyId()) return;
      tokenStore.setSelectedPropertyId(entityId);
      // Nuke every cached query — different property = different data everywhere.
      void queryClient.invalidateQueries();
    },
    [queryClient],
  );

  return {
    properties,
    selectedProperty,
    selectedPropertyId,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
    switchTo,
  };
}
